import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import KeyIcon from '@mui/icons-material/Key';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import WindowsIcon from '@mui/icons-material/Window';
import { EnterprisesAPI } from '../api/enterprises';

function formatBuildDate(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

export default function AgentDownloadDialog({ open, onClose }) {
  const [installer, setInstaller] = useState(null);
  const [setupCode, setSetupCode] = useState('');
  const [hasSetupCode, setHasSetupCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creatingCode, setCreatingCode] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [installerResult, codeResult] = await Promise.allSettled([
        EnterprisesAPI.getWindowsInstaller(),
        EnterprisesAPI.getMyToken(),
      ]);
      if (installerResult.status === 'fulfilled') setInstaller(installerResult.value.data || null);
      else throw installerResult.reason;
      setHasSetupCode(codeResult.status === 'fulfilled' && Boolean(codeResult.value.data?.token_hint));
    } catch (err) {
      setInstaller(null);
      setError(err?.response?.data?.error || 'Unable to check installer availability.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setInstaller(null);
    setSetupCode('');
    void load();
  }, [open]);

  const download = async () => {
    try {
      setDownloading(true);
      setError('');
      const result = await EnterprisesAPI.downloadWindowsInstaller();
      const fileName = installer?.installer?.file_name || 'SushaAgent-x64.msi';
      const url = URL.createObjectURL(new Blob([result.data], { type: 'application/x-msi' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.error || 'The installer is not available. Please refresh and try again.');
    } finally {
      setDownloading(false);
    }
  };

  const createSetupCode = async () => {
    if (hasSetupCode && !window.confirm('Create a new company setup code? New installations using the previous code will stop. Devices already enrolled will continue reporting.')) return;
    try {
      setCreatingCode(true);
      setError('');
      const result = await EnterprisesAPI.regenerateMyToken();
      const nextCode = result.data?.token || '';
      if (!nextCode) throw new Error('No setup code was returned.');
      setSetupCode(nextCode);
      setHasSetupCode(true);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Unable to create a setup code.');
    } finally {
      setCreatingCode(false);
    }
  };

  const copySetupCode = async () => {
    try { await navigator.clipboard.writeText(setupCode); }
    catch { setError('Copy failed. Select and copy the setup code manually.'); }
  };

  const ready = Boolean(installer?.available);
  const busy = loading || downloading || creatingCode;

  return <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 1.5 }}>
      <MonitorHeartOutlinedIcon color="primary" />
      <Box>
        <Typography variant="h6" fontWeight={800}>Install Susha Agent</Typography>
        <Typography variant="body2" color="text.secondary">Keep your company devices visible, secure, and up to date.</Typography>
      </Box>
      <IconButton aria-label="Close" onClick={onClose} disabled={busy} sx={{ ml: 'auto' }}><CloseIcon /></IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <Stack spacing={2.25}>
        {error && <Alert severity="error">{error}</Alert>}
        <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2.5, borderColor: ready ? 'success.light' : 'divider' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: ready ? '#eff6ff' : '#f8fafc', color: ready ? 'primary.main' : 'text.disabled' }}><WindowsIcon /></Box>
              <Box>
                <Typography fontWeight={800}>Windows installer</Typography>
                <Typography variant="body2" color="text.secondary">
                  {loading ? 'Checking availability…' : ready ? `Ready to download${installer?.installer?.agent_version ? ` • Version ${installer.installer.agent_version}` : ''}` : 'Not ready yet'}
                </Typography>
              </Box>
            </Stack>
            {ready && <Button variant="contained" startIcon={<DownloadIcon />} onClick={download} disabled={downloading}>{downloading ? 'Downloading…' : 'Download MSI'}</Button>}
          </Stack>
          {ready && <Typography variant="caption" color="text.secondary" display="block" mt={1.25}>Prepared {formatBuildDate(installer.installer?.built_at)}. This installer is configured for your company.</Typography>}
        </Paper>

        {ready ? <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2.5, bgcolor: '#f8fbff' }}>
          <Typography fontWeight={800}>Three simple steps</Typography>
          <Stack spacing={1} mt={1.25}>
            <Typography variant="body2">1. Download the MSI installer.</Typography>
            <Typography variant="body2">2. Create a company setup code, then run the installer as an administrator.</Typography>
            <Typography variant="body2">3. Enter the code when requested, then open <b>Monitor</b> after the first scan.</Typography>
          </Stack>
        </Paper> : <Alert severity="info">Your company installer is being prepared. Refresh this page in a few minutes, or contact your IT administrator if it remains unavailable.</Alert>}

        {ready && <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.25}>
            <Box><Typography fontWeight={800}>Company setup code</Typography><Typography variant="body2" color="text.secondary">Only the person installing the agent needs this code.</Typography></Box>
            {!setupCode && <Button variant="outlined" startIcon={<KeyIcon />} onClick={createSetupCode} disabled={creatingCode}>{creatingCode ? 'Creating…' : hasSetupCode ? 'Replace setup code' : 'Create setup code'}</Button>}
          </Stack>
          {setupCode && <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: '#f8fafc' }}><Typography variant="caption" color="text.secondary">Copy this now. It is not shown again after you close this window.</Typography><Typography sx={{ mt: .5, fontFamily: 'monospace', fontWeight: 800, wordBreak: 'break-all' }}>{setupCode}</Typography><Button size="small" startIcon={<ContentCopyIcon />} onClick={copySetupCode} sx={{ mt: 1 }}>Copy setup code</Button></Box>}
        </Paper>}

        <Alert severity="success">After installation, the agent automatically records hardware, storage, and installed software, then checks in every four hours.</Alert>
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 2 }}>
      <Button onClick={onClose} disabled={busy}>Close</Button>
      <Button startIcon={<RefreshIcon />} onClick={load} disabled={busy}>Refresh</Button>
    </DialogActions>
  </Dialog>;
}
